import React, { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { tokens } from '../../../../theme/tokens';
import { StagedSourceBadge } from '../../../StagedSourceBadge';
import { FormDialog } from '../../../ui';
import { CountBubble, DetailCard, EMPTY, LinkButton, SelectInput, TabBody, TextInput } from '../primitives';
import type { ImageDraft, ProductDraft } from '../productDraft';
import { hostOf } from '../productDetailFormat';

/** `ProductImageType` / `ProductVideo.type` in src/types/canonicalProduct.ts. */
export const IMAGE_TYPES = ['main', 'alternate', 'lifestyle', 'detail', 'swatch', 'promotional', 'icon'];
const VIDEO_TYPES = ['product', 'tutorial', 'review'];
const DOC_TYPES = ['manual', 'firmware', 'driver', 'spec-sheet', 'brochure', 'support-page', 'warranty', 'other'];

interface Props {
  draft: ProductDraft;
  editing: boolean;
  update: (patch: Partial<ProductDraft>) => void;
  startEditing: () => void;
  toast: (message: string) => void;
}

const typeBadge = (image: ImageDraft) => {
  if (!image.type) return null;
  if (image.typeSource === 'admin') return { source: 'manual' as const, detail: 'Type set by an admin' };
  if (image.typeSource === 'ai') return { source: 'ai' as const, detail: 'Type set by the image classifier' };
  return { source: 'motzu' as const, detail: 'Type set by the image heuristics' };
};

export const MediaTab: React.FC<Props> = ({ draft, editing, update, startEditing, toast }) => {
  const [adding, setAdding] = useState<null | 'image' | 'video' | 'doc'>(null);
  const [form, setForm] = useState({ url: '', title: '', type: '', language: '' });

  const setImages = (images: ImageDraft[]) => update({ images });
  const openAdd = (kind: 'image' | 'video' | 'doc') => {
    if (!editing) startEditing();
    setForm({ url: '', title: '', type: kind === 'video' ? 'product' : kind === 'doc' ? 'manual' : '', language: kind === 'doc' ? 'EN' : '' });
    setAdding(kind);
  };
  const submitAdd = () => {
    const url = form.url.trim();
    if (!url) return;
    if (adding === 'image') setImages([...draft.images, { url, alt: '', isPrimary: draft.images.length === 0, type: '', typeSource: '', imageSource: 'admin' }]);
    if (adding === 'video') update({ videos: [...draft.videos, { url, title: form.title.trim(), type: form.type }] });
    if (adding === 'doc') update({ documentation: [...draft.documentation, { url, title: form.title.trim() || url, type: form.type, language: form.language }] });
    setAdding(null);
  };

  return (
    <TabBody>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Images</Typography>
        <CountBubble>{draft.images.length}</CountBubble>
        <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>Type is set by AI unless marked manual</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => openAdd('image')}
          sx={{ ml: 'auto !important', height: 30, borderRadius: `${tokens.radius.small}px`, fontSize: 12.5, fontWeight: 600, color: tokens.ink.body, borderColor: tokens.line.default }}
        >
          Add from URL
        </Button>
      </Stack>

      {draft.images.length === 0 ? (
        <DetailCard>
          <Typography sx={{ p: 3, textAlign: 'center', fontSize: 13, color: tokens.ink.faint }}>No photos yet. Add one from a URL.</Typography>
        </DetailCard>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fill, minmax(180px, 1fr))' }, gap: 1.5 }}>
          {draft.images.map((image, i) => {
            const badge = typeBadge(image);
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: `1px solid ${image.isPrimary ? tokens.accent.border : tokens.line.default}`,
                  borderRadius: `${tokens.radius.card}px`,
                  overflow: 'hidden',
                  bgcolor: tokens.surface.card,
                }}
              >
                <Box sx={{ position: 'relative', aspectRatio: '1 / 1', bgcolor: tokens.surface.sunken }}>
                  <Box component="img" src={image.url} alt={image.alt} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <Box
                    component="span"
                    sx={{ position: 'absolute', top: 8, left: 8, px: '7px', py: '1px', borderRadius: `${tokens.radius.chip}px`, bgcolor: tokens.surface.card, border: `1px solid ${tokens.line.default}`, color: tokens.ink.bodyAlt, fontSize: 11, fontWeight: 600 }}
                  >
                    {i + 1}
                  </Box>
                  {image.isPrimary && (
                    <Box
                      component="span"
                      sx={{ position: 'absolute', top: 8, right: 8, px: 1, py: '2px', borderRadius: `${tokens.radius.chip}px`, bgcolor: tokens.accent.main, color: tokens.ink.inverse, fontSize: 11, fontWeight: 600 }}
                    >
                      Primary
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: '11px', py: '9px', borderTop: `1px solid ${tokens.line.hairline}` }}>
                  {editing && (
                    <TextInput
                      ariaLabel={`URL of photo ${i + 1}`}
                      value={image.url}
                      placeholder="https://…"
                      onChange={(url) => setImages(draft.images.map((img, j) => (j === i ? { ...img, url } : img)))}
                      sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
                    />
                  )}
                  {editing ? (
                    <TextInput
                      ariaLabel={`Alt text for photo ${i + 1}`}
                      value={image.alt}
                      placeholder="Alt text"
                      onChange={(alt) => setImages(draft.images.map((img, j) => (j === i ? { ...img, alt } : img)))}
                      sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
                    />
                  ) : (
                    <Typography noWrap title={image.alt} sx={{ fontSize: 12, color: image.alt ? tokens.ink.body : tokens.ink.faint }}>
                      {image.alt || 'No alt text'}
                    </Typography>
                  )}
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                    {editing ? (
                      <SelectInput
                        ariaLabel={`Type of photo ${i + 1}`}
                        value={image.type}
                        options={IMAGE_TYPES}
                        onChange={(type) => setImages(draft.images.map((img, j) => (j === i ? { ...img, type, typeSource: 'admin' } : img)))}
                      />
                    ) : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 19, px: 0.75, borderRadius: '5px', bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt, fontSize: 11, fontWeight: 600 }}>
                        {image.type || 'untyped'}
                      </Box>
                    )}
                    <StagedSourceBadge source={badge} />
                    <Typography component="span" sx={{ ml: 'auto !important', fontSize: 11, color: tokens.ink.faint }}>
                      {hostOf(image.url) || EMPTY}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    {!image.isPrimary && (
                      <LinkButton
                        sx={{ fontSize: 12 }}
                        onClick={() => {
                          if (!editing) startEditing();
                          setImages(draft.images.map((img, j) => ({ ...img, isPrimary: j === i })));
                          toast('Primary photo updated');
                        }}
                      >
                        Make primary
                      </LinkButton>
                    )}
                    <LinkButton
                      sx={{ ml: 'auto !important', fontSize: 12, color: tokens.ink.muted, '&:hover': { color: tokens.signal.problem.ink, bgcolor: 'transparent' } }}
                      onClick={() => {
                        if (image.isPrimary) {
                          toast('Pick a new primary first');
                          return;
                        }
                        if (!editing) startEditing();
                        setImages(draft.images.filter((_, j) => j !== i));
                        toast('Photo removed');
                      }}
                    >
                      Remove
                    </LinkButton>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
          <DetailCard title="Videos" count={draft.videos.length} actions={<LinkButton onClick={() => openAdd('video')}>Add video</LinkButton>}>
            <MediaRows
              rows={draft.videos.map((v) => ({ title: v.title || v.url, url: v.url, type: v.type }))}
              empty="No videos."
              editing={editing}
              onRemove={(i) => update({ videos: draft.videos.filter((_, j) => j !== i) })}
            />
          </DetailCard>
        </Box>
        <Box sx={{ flex: '1 1 380px', minWidth: 0 }}>
          <DetailCard title="Documents" count={draft.documentation.length} actions={<LinkButton onClick={() => openAdd('doc')}>Add document</LinkButton>}>
            <MediaRows
              rows={draft.documentation.map((d) => ({ title: d.title || d.url, url: d.url, type: d.type, lang: d.language }))}
              empty="No documents."
              editing={editing}
              onRemove={(i) => update({ documentation: draft.documentation.filter((_, j) => j !== i) })}
            />
          </DetailCard>
        </Box>
      </Box>

      <FormDialog
        open={adding !== null}
        title={adding === 'image' ? 'Add a photo' : adding === 'video' ? 'Add a video' : 'Add a document'}
        submitLabel="Add"
        onClose={() => setAdding(null)}
        onSubmit={submitAdd}
        submitDisabled={!form.url.trim()}
      >
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <TextInput ariaLabel="URL" placeholder="https://…" value={form.url} onChange={(url) => setForm((f) => ({ ...f, url }))} />
          {adding !== 'image' && <TextInput ariaLabel="Title" placeholder="Title" value={form.title} onChange={(title) => setForm((f) => ({ ...f, title }))} />}
          {adding === 'video' && <SelectInput ariaLabel="Video type" value={form.type} options={VIDEO_TYPES} onChange={(type) => setForm((f) => ({ ...f, type }))} />}
          {adding === 'doc' && (
            <Stack direction="row" spacing={1}>
              <SelectInput ariaLabel="Document type" value={form.type} options={DOC_TYPES} onChange={(type) => setForm((f) => ({ ...f, type }))} />
              <SelectInput ariaLabel="Language" value={form.language} options={['EN', 'FR']} onChange={(language) => setForm((f) => ({ ...f, language }))} />
            </Stack>
          )}
          <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>Added to your edits. Nothing is saved until you save changes.</Typography>
        </Stack>
      </FormDialog>
    </TabBody>
  );
};

const MediaRows: React.FC<{
  rows: Array<{ title: string; url: string; type?: string; lang?: string }>;
  empty: string;
  editing: boolean;
  onRemove: (index: number) => void;
}> = ({ rows, empty, editing, onRemove }) => (
  <Box sx={{ pt: 0.5 }}>
    {rows.length === 0 && <Typography sx={{ px: '18px', pb: 2, fontSize: 13, color: tokens.ink.faint }}>{empty}</Typography>}
    {rows.map((r, i) => (
      <Box key={`${r.url}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 48, px: '18px', py: 0.75, borderTop: `1px solid ${tokens.line.hairline}`, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Box component="a" href={r.url} target="_blank" rel="noreferrer" sx={{ fontSize: 13, fontWeight: 600, color: tokens.accent.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {r.title}
          </Box>
          <Typography noWrap sx={{ fontSize: 11.5, color: tokens.ink.faint }}>{r.url.replace(/^https?:\/\//, '')}</Typography>
        </Box>
        {r.type && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 19, px: 0.75, borderRadius: '5px', bgcolor: tokens.surface.tint, color: tokens.ink.bodyAlt, fontSize: 11, fontWeight: 600 }}>
            {r.type.replace(/-/g, ' ')}
          </Box>
        )}
        {r.lang && <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: tokens.ink.muted }}>{r.lang}</Typography>}
        {editing && (
          <LinkButton sx={{ fontSize: 12, color: tokens.ink.muted, '&:hover': { color: tokens.signal.problem.ink, bgcolor: 'transparent' } }} onClick={() => onRemove(i)}>
            Remove
          </LinkButton>
        )}
      </Box>
    ))}
  </Box>
);
